import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { 
    FileText, Users, MapPin, Trophy,  
    Trash2, BrainCircuit, Download, CheckCircle, Loader2, 
    Zap, Target, Activity, ChevronRight, Lock, History, FileJson,
    PieChart, Layout, Sparkles, Clock, Film, Tag
} from 'lucide-react';

import { 
    AppButton, 
    BlackboxLoaderIcon 
} from '../../components/SharedUI';
import { GENRES, BUDGET_LEVELS } from '../../config/constants';
import { generateAudienceAnalysisLLM } from '../../services/aiService';

// TODO(West): move this into ../../config/constants alongside GENRES/BUDGET_LEVELS
// once you've confirmed the label set — kept local here so this file works standalone.
const RELEASE_TYPES = [
    'Theatrical Wide',
    'Theatrical Limited / Platform',
    'Festival Premiere',
    'Streaming / VOD',
    'Hybrid (Theatrical + Streaming)',
    'TBD'
];

// Campaign lead time drives which Social/PR Plan phases (awareness, pre-sale, opening,
// post-release) actually fit before release. Auto-derives from Target Release Date,
// but stays a manual, overridable field since release dates move and festival-only
// titles may not have one yet.
const weeksBetween = (fromISODate, toISODate) => {
    if (!fromISODate || !toISODate) return null;
    const ms = new Date(toISODate) - new Date(fromISODate);
    if (Number.isNaN(ms)) return null;
    return Math.max(0, Math.round(ms / (7 * 24 * 60 * 60 * 1000)));
};

// --- FORM FIELD COMPONENTS ---
const FieldLabel = ({ children }) => (
    <label className="block text-[10px] font-bold text-zinc-500 uppercase tracking-widest mb-1.5 group-focus-within:text-amber-400 transition-colors">
        {children}
    </label>
);

const TechInput = ({ label, ...props }) => (
    <div className="group">
        <FieldLabel>{label}</FieldLabel>
        <input 
            {...props}
            className="w-full bg-zinc-900/60 border border-zinc-800 text-zinc-200 text-sm px-3 py-2.5 rounded-lg focus:outline-none focus:ring-1 focus:ring-amber-500/60 focus:border-amber-500/40 transition-all placeholder:text-zinc-700"
        />
    </div>
);

const TechSelect = ({ label, children, ...props }) => (
    <div className="group">
        <FieldLabel>{label}</FieldLabel>
        <div className="relative">
            <select 
                {...props}
                className="w-full appearance-none bg-zinc-900/60 border border-zinc-800 text-zinc-200 text-sm px-3 py-2.5 rounded-lg focus:outline-none focus:ring-1 focus:ring-amber-500/60 focus:border-amber-500/40 transition-all cursor-pointer"
            >
                {children}
            </select>
            <ChevronRight size={12} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-zinc-600 rotate-90" />
        </div>
    </div>
);

const TechTextarea = ({ label, ...props }) => (
    <div className="group">
        <FieldLabel>{label}</FieldLabel>
        <textarea 
            {...props}
            className="w-full bg-zinc-900/60 border border-zinc-800 text-zinc-200 text-sm px-3 py-2.5 rounded-lg focus:outline-none focus:ring-1 focus:ring-amber-500/60 focus:border-amber-500/40 transition-all placeholder:text-zinc-700 resize-none"
        />
    </div>
);

// --- LOADING OVERLAY ---
const PremiumAnalysisOverlay = ({ isVisible, currentStepIndex }) => {
    if (!isVisible) return null;

    const steps = [
        "Ingesting Film Metadata...",
        "Deconstructing Narrative Tropes...",
        "Querying Audience Clustering Engine...",
        "Modeling Algorithmic Formats...",
        "Synthesizing Creative Matrix...",
        "Finalizing Andromeda Dossier..."
    ];
    const progress = Math.min(((currentStepIndex + 1) / steps.length) * 100, 100);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-950/95 backdrop-blur-xl animate-in fade-in duration-300">
            <div className="w-full max-w-md p-8 relative">
                <div className="flex justify-center mb-10">
                    <div className="relative">
                        <div className="absolute inset-0 bg-amber-500 blur-2xl opacity-10 animate-pulse rounded-full" />
                        <BlackboxLoaderIcon className="w-16 h-16 relative z-10 text-white" variant="white-pulse" />
                    </div>
                </div>
                <div className="relative w-full h-0.5 bg-zinc-800 rounded-full mb-8 overflow-hidden">
                    <div 
                        className="h-full bg-amber-500 transition-all duration-700 ease-out"
                        style={{ width: `${progress}%` }}
                    />
                </div>
                <div className="space-y-3 font-mono text-xs">
                    {steps.map((step, index) => {
                        const isDone = index < currentStepIndex;
                        const isCurrent = index === currentStepIndex;
                        return (
                            <div key={index} className={`flex items-center gap-3 transition-all duration-300 ${isCurrent ? 'translate-x-1' : ''}`}>
                                <div className={`w-4 h-4 rounded-full flex items-center justify-center border flex-shrink-0 ${isDone ? 'border-amber-500/50 bg-amber-500/10 text-amber-400' : isCurrent ? 'border-white bg-white text-black' : 'border-zinc-800 bg-zinc-900'}`}>
                                    {isDone && <CheckCircle size={9} />}
                                    {isCurrent && <Loader2 size={9} className="animate-spin" />}
                                </div>
                                <span className={isCurrent ? "text-white font-bold" : isDone ? "text-amber-400/40" : "text-zinc-700"}>
                                    {step}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

// --- DOSSIER SECTION NAV (vertical rail) ---
const REPORT_SECTIONS = [
    { key: 'summary',   label: 'Summary',       icon: FileText  },
    { key: 'audience',  label: 'Audience',       icon: Users     },
    { key: 'matrix',    label: 'Creative Matrix',icon: Zap       },
    { key: 'markets',   label: 'Markets',        icon: MapPin    },
    { key: 'festivals', label: 'Festivals',      icon: Trophy    },
];

const SectionNav = ({ active, onChange }) => (
    <nav className="flex flex-col gap-0.5 w-[140px] flex-shrink-0 pt-1">
        {REPORT_SECTIONS.map(({ key, label, icon: Icon }) => {
            const isActive = active === key;
            return (
                <button
                    key={key}
                    onClick={() => onChange(key)}
                    className={`group flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-left transition-all text-xs font-medium ${
                        isActive 
                            ? 'bg-amber-500/15 text-amber-300 border border-amber-500/25' 
                            : 'text-zinc-500 hover:text-zinc-300 hover:bg-white/5 border border-transparent'
                    }`}
                >
                    <Icon size={13} className={`flex-shrink-0 ${isActive ? 'text-amber-400' : 'text-zinc-600 group-hover:text-zinc-400'}`} />
                    {label}
                </button>
            );
        })}
    </nav>
);

// --- STAT CARD ---
const StatCard = ({ label, value, icon: Icon, accent = "amber" }) => {
    const accentMap = {
        amber: "text-amber-400 bg-amber-500/10",
        blue:  "text-blue-400 bg-blue-500/10",
        green: "text-emerald-400 bg-emerald-500/10",
        purple:"text-purple-400 bg-purple-500/10",
    };
    return (
        <div className="bg-zinc-900/40 border border-zinc-800 p-4 rounded-xl flex items-start gap-3">
            <div className={`p-2 rounded-lg ${accentMap[accent] || accentMap.amber}`}>
                <Icon size={14} />
            </div>
            <div className="min-w-0">
                <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest mb-1">{label}</p>
                <p className="text-sm font-medium text-zinc-200 leading-snug truncate">{value}</p>
            </div>
        </div>
    );
};

// --- EMPTY STATE ---
const EmptyDossier = ({ onScrollToForm }) => (
    <div className="flex-1 flex flex-col items-center justify-center text-center px-8 py-16">
        <div className="w-20 h-20 bg-zinc-900/60 rounded-full flex items-center justify-center mb-6 border border-zinc-800 relative">
            <div className="absolute inset-0 border border-amber-500/10 rounded-full animate-[ping_4s_infinite]" />
            <Film className="w-8 h-8 text-zinc-600" />
        </div>
        <h3 className="text-xl font-bold text-white mb-3 tracking-tight">No Dossier Yet</h3>
        <p className="text-zinc-500 max-w-xs text-sm leading-relaxed mb-6">
            Fill in your project parameters and run an Andromeda analysis to generate your creative strategy dossier.
        </p>
        <button 
            onClick={onScrollToForm}
            className="text-xs text-amber-400 border border-amber-500/30 px-4 py-2 rounded-lg hover:bg-amber-500/10 transition-colors"
        >
            Set up project →
        </button>
    </div>
);

// --- SECTION RENDERERS ---

const SectionSummary = ({ data, filmData }) => (
    <div className="space-y-5">
        <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-5 relative overflow-hidden">
            <div className="absolute top-3 right-4 opacity-5">
                <FileText size={80} />
            </div>
            <h3 className="text-xs font-bold text-zinc-500 uppercase tracking-widest mb-4">Executive Summary</h3>
            <ul className="space-y-3">
                {data.executive_summary?.map((item, i) => (
                    <li key={i} className="flex gap-3 text-zinc-300 text-sm leading-relaxed">
                        <span className="text-amber-500 font-bold mt-0.5 flex-shrink-0">›</span>
                        {item}
                    </li>
                ))}
            </ul>
        </div>
        <div className="grid grid-cols-2 gap-3">
            <StatCard label="Primary Genre" value={filmData.genre || '—'} icon={Film} accent="blue" />
            <StatCard label="Budget Tier" value={filmData.budgetLevel || '—'} icon={Target} accent="green" />
        </div>
    </div>
);

const SectionAudience = ({ data }) => (
    <div className="space-y-5">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-5">
                <p className="text-[10px] font-bold text-amber-400/70 uppercase tracking-widest mb-3">Primary Audience</p>
                <p className="text-white text-sm leading-relaxed font-medium">
                    {data.target_audience_breakdown?.primary_audience_profile}
                </p>
            </div>
            <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-5">
                <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest mb-3">Secondary Audience</p>
                <p className="text-zinc-300 text-sm leading-relaxed">
                    {data.target_audience_breakdown?.secondary_audience_profile}
                </p>
            </div>
        </div>

        {data.target_audience_breakdown?.psychographics?.length > 0 && (
            <div>
                <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest mb-3 flex items-center gap-2">
                    <Tag size={10} /> Psychographic Clusters
                </p>
                <div className="flex flex-wrap gap-2">
                    {data.target_audience_breakdown.psychographics.map(tag => (
                        <span key={tag} className="px-3 py-1.5 rounded-md bg-zinc-800/80 border border-zinc-700/60 text-zinc-300 text-xs font-medium">
                            {tag}
                        </span>
                    ))}
                </div>
            </div>
        )}
    </div>
);

const SectionMatrix = ({ data }) => (
    <div className="space-y-6">
        {/* Asset Allocation */}
        {data.asset_allocation && (
            <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-5">
                    <Activity size={14} className="text-blue-400" />
                    <h3 className="text-xs font-bold text-zinc-400 uppercase tracking-widest">Asset Allocation</h3>
                </div>
                <div className="w-full h-3 rounded-full bg-zinc-950 flex overflow-hidden mb-4 border border-zinc-800/60">
                    <div style={{ width: `${data.asset_allocation.vertical_video_percent || 0}%` }} className="bg-amber-500" title="Vertical Video" />
                    <div style={{ width: `${data.asset_allocation.carousel_percent || 0}%` }} className="bg-blue-500" title="Carousels" />
                    <div style={{ width: `${data.asset_allocation.static_image_percent || 0}%` }} className="bg-emerald-500" title="Static Images" />
                </div>
                <div className="grid grid-cols-3 gap-3 mb-4 text-center">
                    <div>
                        <p className="text-xl font-black text-amber-400">{data.asset_allocation.vertical_video_percent || 0}%</p>
                        <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest mt-1">Vertical</p>
                    </div>
                    <div>
                        <p className="text-xl font-black text-blue-400">{data.asset_allocation.carousel_percent || 0}%</p>
                        <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest mt-1">Carousel</p>
                    </div>
                    <div>
                        <p className="text-xl font-black text-emerald-400">{data.asset_allocation.static_image_percent || 0}%</p>
                        <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest mt-1">Static</p>
                    </div>
                </div>
                <p className="text-xs text-zinc-400 italic bg-black/30 px-4 py-3 rounded-lg border border-white/5 leading-relaxed">
                    "{data.asset_allocation.rationale}"
                </p>
            </div>
        )}

        {/* Creative Hooks */}
        <div>
            <div className="flex items-center gap-2 mb-4">
                <Target size={14} className="text-amber-400" />
                <h3 className="text-xs font-bold text-zinc-400 uppercase tracking-widest">Core Narrative Hooks</h3>
            </div>
            {data.creative_matrix?.length > 0 ? (
                <div className="space-y-3">
                    {data.creative_matrix.map((hook, index) => (
                        <div key={index} className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-5 hover:border-amber-500/20 transition-colors group">
                            <div className="flex items-start gap-4">
                                <div className="w-7 h-7 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-400 font-bold text-xs flex-shrink-0 mt-0.5">
                                    {String(index + 1).padStart(2, '0')}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h4 className="font-bold text-white text-sm mb-3 group-hover:text-amber-200 transition-colors">{hook.hook_name}</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div>
                                            <p className="text-[10px] font-bold text-zinc-600 uppercase tracking-widest mb-1">Trigger</p>
                                            <p className="text-xs text-zinc-400 leading-relaxed">{hook.psychological_trigger}</p>
                                        </div>
                                        <div>
                                            <p className="text-[10px] font-bold text-zinc-600 uppercase tracking-widest mb-1">Visual</p>
                                            <p className="text-xs text-zinc-400 leading-relaxed">{hook.visual_direction}</p>
                                        </div>
                                        <div>
                                            <p className="text-[10px] font-bold text-zinc-600 uppercase tracking-widest mb-1">Copy Tone</p>
                                            <p className="text-xs text-zinc-300 leading-relaxed italic border-l-2 border-amber-500/40 pl-2">{hook.copy_angle}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="p-8 border-2 border-dashed border-zinc-800 rounded-xl text-center text-zinc-500">
                    <Layout size={28} className="mx-auto mb-3 opacity-40" />
                    <p className="text-sm">Creative matrix not available in this snapshot.</p>
                </div>
            )}
        </div>
    </div>
);

const SectionMarkets = ({ data }) => (
    <div>
        <h3 className="text-xs font-bold text-zinc-500 uppercase tracking-widest mb-4 flex items-center gap-2">
            <MapPin size={12} /> Key Theatrical Markets
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {data.top_us_theatrical_markets?.map((market, i) => (
                <div key={market} className="flex items-center gap-3 p-3 bg-zinc-900/40 border border-zinc-800 rounded-lg hover:border-zinc-700 transition-colors">
                    <div className="w-5 h-5 rounded bg-zinc-800 flex items-center justify-center text-[9px] font-bold text-zinc-500 flex-shrink-0">
                        {i + 1}
                    </div>
                    <span className="text-sm text-zinc-300 font-medium truncate">{market}</span>
                </div>
            ))}
        </div>
    </div>
);

const SectionFestivals = ({ data }) => (
    <div className="space-y-6">
        {[1, 2, 3].map(tier => {
            const fests = data.top_film_festivals?.[`tier_${tier}`];
            if (!fests?.length) return null;
            return (
                <div key={tier}>
                    <div className="flex items-center gap-3 mb-3">
                        <div className="h-px bg-zinc-800 flex-1" />
                        <span className="text-[10px] font-bold text-amber-400 uppercase tracking-widest bg-amber-500/10 px-3 py-1 rounded-full border border-amber-500/20">
                            Tier {tier}
                        </span>
                        <div className="h-px bg-zinc-800 flex-1" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {fests.map(fest => (
                            <div key={fest.name} className="group bg-zinc-900/40 border border-zinc-800 p-4 rounded-xl hover:border-amber-500/20 transition-colors">
                                <div className="flex justify-between items-start mb-2">
                                    <h4 className="font-semibold text-white text-sm group-hover:text-amber-200 transition-colors">{fest.name}</h4>
                                    <Trophy size={13} className="text-zinc-600 group-hover:text-amber-500 flex-shrink-0 ml-2" />
                                </div>
                                <div className="flex justify-between text-xs text-zinc-500 pt-2 border-t border-zinc-800">
                                    <span className="flex items-center gap-1.5"><Clock size={10} /> {fest.dates}</span>
                                    <span className="font-mono text-zinc-400">DL: {fest.deadline}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            );
        })}
    </div>
);

// --- HISTORY SIDEBAR ---
const HistoryEntry = ({ entry, isActive, onSelect, onDelete }) => (
    <div className="group flex items-center gap-1.5">
        <button 
            onClick={() => onSelect(entry.id)} 
            className={`flex-1 text-left px-3 py-2 rounded-lg transition-all border ${
                isActive 
                    ? 'bg-amber-500/15 text-amber-200 border-amber-500/25' 
                    : 'bg-zinc-900/50 text-zinc-400 border-transparent hover:bg-zinc-800 hover:text-zinc-300'
            }`}
        >
            <span className="font-semibold text-xs block truncate">{entry.inputs.title}</span>
            <span className="text-[10px] opacity-50 flex items-center gap-1 mt-0.5">
                <Clock size={8} /> {new Date(entry.created_at).toLocaleDateString()}
            </span>
        </button>
        <button 
            onClick={() => onDelete(entry.id)} 
            className="p-1.5 text-zinc-700 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100 flex-shrink-0"
            title="Delete snapshot"
        >
            <Trash2 size={11} />
        </button>
    </div>
);

// --- MAIN COMPONENT ---
const AudienceAnalysisTab = ({ filmData, setFilmData, setNotification, setConfirmModal, saveFilmData, credits, deductCredit }) => {
    const [loading, setLoading] = useState(false);
    const [loadingStepIndex, setLoadingStepIndex] = useState(0);
    const [selectedAnalysisId, setSelectedAnalysisId] = useState(null);
    
    // FIX: activeReportTab is now independent of which analysis is selected
    // Switching snapshots no longer resets the tab
    const [activeReportTab, setActiveReportTab] = useState('summary');
    
    const formRef = useRef(null);
    const resultsRef = useRef(null);

    const savedAnalyses = useMemo(() => filmData.ai_analyses || [], [filmData.ai_analyses]);

    const activeAnalysisData = useMemo(() => {
        if (savedAnalyses.length === 0) return null;
        const found = savedAnalyses.find(a => a.id === selectedAnalysisId);
        const analysis = found || savedAnalyses[savedAnalyses.length - 1];
        return analysis?.analysis || null;
    }, [savedAnalyses, selectedAnalysisId]);

    // Auto-select latest — only runs when no ID is selected
    useEffect(() => {
        if (savedAnalyses.length > 0 && !selectedAnalysisId) {
            setSelectedAnalysisId(savedAnalyses[savedAnalyses.length - 1].id);
        }
    }, [savedAnalyses, selectedAnalysisId]);

    useEffect(() => {
        let interval;
        if (loading) {
            setLoadingStepIndex(0);
            interval = setInterval(() => {
                setLoadingStepIndex(prev => (prev < 5 ? prev + 1 : prev));
            }, 2000);
        }
        return () => clearInterval(interval);
    }, [loading]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFilmData(prev => ({ ...prev, [name]: value }));
    };

    const handleAnalyze = async () => {
        if (credits < 5) {
            setNotification({ type: 'error', message: 'Insufficient credits for analysis.' });
            return;
        }
        if (!filmData.title || !filmData.logline) {
            setNotification({ type: 'error', message: 'Title and Logline are required.' });
            return;
        }

        setLoading(true);
        try {
            const result = await generateAudienceAnalysisLLM(filmData);
            setLoadingStepIndex(6);
            await new Promise(resolve => setTimeout(resolve, 800));

            const newEntry = {
                id: `analysis_${Date.now()}`,
                created_at: new Date().toISOString(),
                inputs: {
                    title: filmData.title,
                    logline: filmData.logline,
                    genre: filmData.genre,
                    budgetLevel: filmData.budgetLevel,
                    releaseType: filmData.releaseType || 'TBD',
                    leadTimeWeeks: filmData.leadTimeWeeks ?? weeksBetween(new Date().toISOString().slice(0, 10), filmData.releaseDate) ?? null
                },
                analysis: result
            };

            const updatedAnalyses = [...(filmData.ai_analyses || []), newEntry];
            const newFilmData = { ...filmData, ai_analyses: updatedAnalyses };
            const savedProject = await saveFilmData(newFilmData);

            if (savedProject) {
                await deductCredit(5);
                setSelectedAnalysisId(newEntry.id);
                setActiveReportTab('matrix');
                setNotification({ type: 'success', message: 'Andromeda analysis complete.' });
                
                setTimeout(() => {
                    if (window.innerWidth < 1024) {
                        document.getElementById('dossier-panel')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    } else {
                        resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                }, 400);
            }
        } catch (error) {
            console.error("Analysis error:", error);
            setNotification({ type: 'error', message: `Analysis failed: ${error.message}` });
        } finally {
            setLoading(false);
        }
    };

    const handleExportPDF = () => {
        try {
            if (!window.jspdf || !activeAnalysisData) {
                setNotification({ type: 'error', message: 'PDF generator not ready or no data.' });
                return;
            }

            const { jsPDF } = window.jspdf;
            const doc = new jsPDF('p', 'pt', 'a4');
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            const margin = 50;
            let y = margin;

            const COLORS = {
                AMBER:   [245, 158, 11],
                BLUE:    [37,  99,  235],
                EMERALD: [16,  185, 129],
                DARK:    [24,  24,  27],
                GRAY:    [113, 113, 122],
                BG_SOFT: [250, 250, 252]
            };

            const checkPageBreak = (needed) => {
                if (y + needed > pageHeight - margin) { doc.addPage(); y = margin; return true; }
                return false;
            };

            const drawSectionHeader = (title, accent = COLORS.AMBER) => {
                checkPageBreak(60);
                y += 20;
                doc.setFillColor(...accent);
                doc.rect(margin, y, 4, 18, 'F');
                doc.setFont("helvetica", "bold");
                doc.setFontSize(14);
                doc.setTextColor(...COLORS.DARK);
                doc.text(title.toUpperCase(), margin + 12, y + 14);
                y += 35;
            };

            doc.setFillColor(...COLORS.DARK);
            doc.rect(0, 0, pageWidth, 140, 'F');
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(24);
            doc.setFont("helvetica", "bold");
            doc.text("CREATIVE STRATEGY DOSSIER", margin, 62);
            doc.setFontSize(11);
            doc.setFont("helvetica", "normal");
            doc.setTextColor(180, 180, 180);
            doc.text(`PROJECT: ${filmData.title?.toUpperCase() || "UNTITLED"}`, margin, 90);
            const metaText = `${filmData.genre} | ${filmData.budgetLevel}`;
            doc.setFillColor(...COLORS.AMBER);
            doc.roundedRect(margin, 104, doc.getTextWidth(metaText) + 20, 18, 3, 3, 'F');
            doc.setTextColor(0, 0, 0);
            doc.setFontSize(9);
            doc.text(metaText, margin + 10, 116);
            y = 175;

            drawSectionHeader("Executive Summary");
            doc.setFont("helvetica", "normal");
            doc.setFontSize(10);
            doc.setTextColor(...COLORS.DARK);
            activeAnalysisData.executive_summary?.forEach(point => {
                const lines = doc.splitTextToSize(`• ${point}`, pageWidth - margin * 2);
                checkPageBreak(lines.length * 15);
                doc.text(lines, margin, y);
                y += lines.length * 14;
            });
            y += 10;

            drawSectionHeader("Target Audience Architecture", COLORS.BLUE);
            const renderProfile = (title, content, color = COLORS.GRAY) => {
                doc.setFont("helvetica", "bold");
                doc.setFontSize(10);
                doc.setTextColor(...color);
                doc.text(title, margin, y);
                y += 15;
                doc.setFont("helvetica", "normal");
                doc.setTextColor(...COLORS.DARK);
                const lines = doc.splitTextToSize(content || '', pageWidth - margin * 2);
                doc.text(lines, margin, y);
                y += (lines.length * 14) + 15;
            };
            renderProfile("PRIMARY AUDIENCE PROFILE", activeAnalysisData.target_audience_breakdown?.primary_audience_profile, COLORS.BLUE);
            renderProfile("SECONDARY AUDIENCE PROFILE", activeAnalysisData.target_audience_breakdown?.secondary_audience_profile);

            drawSectionHeader("Key Theatrical Markets");
            doc.setFontSize(9);
            doc.setTextColor(...COLORS.DARK);
            const markets = activeAnalysisData.top_us_theatrical_markets || [];
            const midpoint = Math.ceil(markets.length / 2);
            for (let i = 0; i < midpoint; i++) {
                checkPageBreak(15);
                doc.text(`${i + 1}. ${markets[i]}`, margin + 10, y);
                if (markets[i + midpoint]) doc.text(`${i + midpoint + 1}. ${markets[i + midpoint]}`, pageWidth / 2 + 10, y);
                y += 14;
            }
            y += 20;

            if (activeAnalysisData.asset_allocation) {
                drawSectionHeader("Algorithmic Asset Allocation", COLORS.EMERALD);
                const alloc = activeAnalysisData.asset_allocation;
                [
                    [`Vertical Video: ${alloc.vertical_video_percent}%`, alloc.vertical_video_percent, COLORS.AMBER],
                    [`Carousels: ${alloc.carousel_percent}%`, alloc.carousel_percent, COLORS.BLUE],
                    [`Static Images: ${alloc.static_image_percent}%`, alloc.static_image_percent, COLORS.EMERALD],
                ].forEach(([label, pct, color]) => {
                    doc.setFont("helvetica", "bold");
                    doc.setFontSize(11);
                    doc.setTextColor(...COLORS.DARK);
                    doc.text(label, margin, y);
                    doc.setFillColor(...color);
                    doc.rect(margin + 160, y - 10, (pct || 0) * 2, 12, 'F');
                    y += 25;
                });
                doc.setFont("helvetica", "italic");
                doc.setFontSize(10);
                doc.setTextColor(...COLORS.GRAY);
                const rLines = doc.splitTextToSize(`Strategy: ${alloc.rationale}`, pageWidth - margin * 2);
                doc.text(rLines, margin, y);
                y += rLines.length * 14 + 20;
            }

            if (activeAnalysisData.creative_matrix?.length > 0) {
                drawSectionHeader("Core Narrative Hooks", COLORS.AMBER);
                activeAnalysisData.creative_matrix.forEach((hook, i) => {
                    checkPageBreak(120);
                    doc.setFillColor(...COLORS.BG_SOFT);
                    doc.roundedRect(margin, y, pageWidth - margin * 2, 110, 4, 4, 'F');
                    doc.setFont("helvetica", "bold");
                    doc.setFontSize(11);
                    doc.setTextColor(...COLORS.AMBER);
                    doc.text(`HOOK ${String(i + 1).padStart(2, '0')}: ${hook.hook_name.toUpperCase()}`, margin + 15, y + 25);
                    doc.setFontSize(9);
                    doc.setTextColor(...COLORS.DARK);
                    y += 45;
                    [["TRIGGER:", hook.psychological_trigger], ["VISUALS:", hook.visual_direction], ["COPY:", hook.copy_angle]].forEach(([prefix, text]) => {
                        doc.setFont("helvetica", "bold");
                        doc.text(prefix, margin + 15, y);
                        doc.setFont("helvetica", "normal");
                        const lines = doc.splitTextToSize(text || '', pageWidth - margin * 2 - 80);
                        doc.text(lines, margin + 70, y);
                        y += lines.length * 12 + 8;
                    });
                    y += 20;
                });
            }

            const pageCount = doc.internal.getNumberOfPages();
            for (let i = 1; i <= pageCount; i++) {
                doc.setPage(i);
                doc.setFontSize(8);
                doc.setTextColor(...COLORS.GRAY);
                doc.text(`BLACKBOX Intelligence Dossier  |  Proprietary  |  Page ${i} of ${pageCount}`, pageWidth / 2, pageHeight - 28, { align: 'center' });
            }

            doc.save(`${filmData.title?.replace(/\s+/g, '_') || 'Project'}_Creative_Dossier.pdf`);
            setNotification({ type: 'success', message: 'Intelligence Dossier exported.' });
        } catch (error) {
            console.error("PDF Export Error:", error);
            setNotification({ type: 'error', message: 'Failed to generate PDF.' });
        }
    };

    const handleExportJSON = () => {
        if (!activeAnalysisData) {
            setNotification({ type: 'error', message: 'No data to export.' });
            return;
        }
        try {
            const blob = new Blob([JSON.stringify(activeAnalysisData, null, 2)], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${filmData.title?.replace(/\s+/g, '_') || 'Project'}_Audience_Genius.json`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            setNotification({ type: 'success', message: 'JSON exported.' });
        } catch (error) {
            setNotification({ type: 'error', message: 'Failed to export JSON.' });
        }
    };

    const handleDeleteSnapshot = (analysisId) => {
        setConfirmModal({
            isOpen: true,
            title: 'Delete Snapshot',
            message: 'This will permanently delete this intelligence report.',
            onConfirm: async () => {
                const updatedAnalyses = (filmData.ai_analyses || []).filter(a => a.id !== analysisId);
                await saveFilmData({ ...filmData, ai_analyses: updatedAnalyses });
                if (selectedAnalysisId === analysisId) {
                    setSelectedAnalysisId(updatedAnalyses[updatedAnalyses.length - 1]?.id || null);
                }
                setNotification({ type: 'info', message: 'Snapshot deleted.' });
                setConfirmModal({ isOpen: false });
            },
            onCancel: () => setConfirmModal({ isOpen: false })
        });
    };

    const renderSection = () => {
        if (!activeAnalysisData) return null;
        switch (activeReportTab) {
            case 'summary':   return <SectionSummary  data={activeAnalysisData} filmData={filmData} />;
            case 'audience':  return <SectionAudience data={activeAnalysisData} />;
            case 'matrix':    return <SectionMatrix   data={activeAnalysisData} />;
            case 'markets':   return <SectionMarkets  data={activeAnalysisData} />;
            case 'festivals': return <SectionFestivals data={activeAnalysisData} />;
            default:          return null;
        }
    };

    const activeSectionLabel = REPORT_SECTIONS.find(s => s.key === activeReportTab)?.label || '';

    return (
        <>
            <PremiumAnalysisOverlay isVisible={loading} currentStepIndex={loadingStepIndex} />

            {/* ROOT: two-column at lg, stacked on mobile */}
            <div className="flex flex-col lg:flex-row gap-0 lg:h-full min-h-screen">

                {/* ── LEFT PANEL: Inputs ──────────────────────────────── */}
                <div 
                    ref={formRef}
                    className="w-full lg:w-[320px] xl:w-[340px] flex-shrink-0 flex flex-col border-r border-zinc-800/60 h-auto lg:h-full lg:overflow-y-auto custom-scrollbar order-1"
                >
                    <div className="p-5 flex flex-col gap-5">
                        {/* Panel header */}
                        <div className="flex items-center gap-2.5 pt-1">
                            <Sparkles size={14} className="text-amber-400" />
                            <h2 className="text-sm font-bold text-white tracking-tight">Andromeda Analysis</h2>
                            <span className="ml-auto text-[10px] text-zinc-600 font-mono bg-zinc-900 px-2 py-0.5 rounded border border-zinc-800">5 credits</span>
                        </div>

                        {/* Project Parameters */}
                        <div className="space-y-4">
                            <p className="text-[10px] font-bold text-zinc-600 uppercase tracking-widest">Project Parameters</p>
                            <TechInput name="title" label="Working Title" placeholder="Enter title..." value={filmData.title || ''} onChange={handleInputChange} />
                            <TechInput
                                name="releaseDate" label="Target Release Date" type="date"
                                value={filmData.releaseDate || ''} onChange={handleInputChange}
                            />
                            {/* Not used by the Dossier itself — this is the one field the Social
                                Playbook's calendar export can't function without. Every relative
                                phase ("Weeks -16 to -12", "post-release week 3-4") gets anchored to
                                a real month/week off this date. Optional here, but the calendar
                                export will refuse to run without it rather than silently guessing. */}
                            <div className="grid grid-cols-2 gap-3">
                                <TechSelect name="genre" label="Genre" value={filmData.genre || 'Action'} onChange={handleInputChange}>
                                    {GENRES.map(g => <option key={g} value={g}>{g}</option>)}
                                </TechSelect>
                                <TechSelect name="budgetLevel" label="Budget Tier" value={filmData.budgetLevel || 'Indie'} onChange={handleInputChange}>
                                    {BUDGET_LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
                                </TechSelect>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <TechSelect
                                    name="releaseType" label="Release Type"
                                    value={filmData.releaseType || 'TBD'}
                                    onChange={handleInputChange}
                                >
                                    {RELEASE_TYPES.map(r => <option key={r} value={r}>{r}</option>)}
                                </TechSelect>
                                <TechInput
                                    name="leadTimeWeeks" label="Campaign Lead Time (wks)" type="number" min="0"
                                    placeholder={String(weeksBetween(new Date().toISOString().slice(0, 10), filmData.releaseDate) ?? '—')}
                                    value={
                                        filmData.leadTimeWeeks ??
                                        weeksBetween(new Date().toISOString().slice(0, 10), filmData.releaseDate) ??
                                        ''
                                    }
                                    onChange={handleInputChange}
                                />
                            </div>
                            <TechTextarea name="logline" label="Logline" placeholder="In a world where..." value={filmData.logline || ''} onChange={handleInputChange} rows={4} />
                            <TechInput name="themes" label="Key Themes (optional)" placeholder="Revenge, Isolation, Hope..." value={filmData.themes || ''} onChange={handleInputChange} />
                            <TechTextarea name="stars" label="Cast & Key Talent (optional)" placeholder="e.g., Naturi Naughton, Lance Gross, Jason Mitchell..." value={filmData.stars || ''} onChange={handleInputChange} rows={2} />
                        </div>

                        {/* Contact */}
                        <div className="pt-1">
                            <div className="flex items-center gap-2 mb-3">
                                <Lock size={10} className="text-zinc-600" />
                                <p className="text-[10px] font-bold text-zinc-600 uppercase tracking-widest">Confidential Contact</p>
                            </div>
                            <TechInput name="contactEmail" label="Inquiries Email" placeholder="contact@studio.com" value={filmData.contactEmail || ''} onChange={handleInputChange} />
                        </div>

                        {/* CTA */}
                        <button 
                            onClick={handleAnalyze} 
                            disabled={loading} 
                            className="group relative w-full py-3.5 bg-amber-500 text-black font-bold rounded-xl overflow-hidden transition-all hover:bg-amber-400 active:scale-[0.98] shadow-[0_4px_20px_rgba(245,158,11,0.25)] disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            <span className="relative z-10 flex items-center justify-center gap-2 uppercase tracking-widest text-xs">
                                <Zap size={14} className={loading ? "animate-pulse" : "fill-current"} />
                                {loading ? "Processing..." : "Generate Creative Matrix"}
                            </span>
                        </button>

                        {/* Snapshot History */}
                        {savedAnalyses.length > 0 && (
                            <div className="border-t border-zinc-800 pt-4">
                                <div className="flex items-center gap-2 mb-3">
                                    <History size={11} className="text-zinc-600" />
                                    <span className="text-[10px] font-bold text-zinc-600 uppercase tracking-widest">Snapshot History</span>
                                    <span className="ml-auto text-[10px] text-zinc-700 font-mono">{savedAnalyses.length}</span>
                                </div>
                                <div className="space-y-1.5 max-h-52 overflow-y-auto custom-scrollbar pr-1">
                                    {[...savedAnalyses].reverse().map((entry) => (
                                        <HistoryEntry 
                                            key={entry.id}
                                            entry={entry}
                                            isActive={selectedAnalysisId === entry.id}
                                            onSelect={setSelectedAnalysisId}
                                            onDelete={handleDeleteSnapshot}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* ── RIGHT PANEL: Dossier ─────────────────────────────── */}
                <div 
                    id="dossier-panel"
                    ref={resultsRef}
                    className="flex-1 flex flex-col h-auto lg:h-full order-2 overflow-hidden"
                >
                    {!activeAnalysisData ? (
                        <EmptyDossier onScrollToForm={() => formRef.current?.scrollIntoView({ behavior: 'smooth' })} />
                    ) : (
                        <div className="flex flex-col h-full">
                            
                            {/* Dossier header bar */}
                            <div className="flex items-center justify-between gap-4 px-6 py-4 border-b border-zinc-800/60 flex-shrink-0">
                                <div className="flex items-center gap-3 min-w-0">
                                    <span className="w-2 h-2 rounded-full bg-amber-400 shadow-[0_0_8px_rgba(245,158,11,0.6)] flex-shrink-0" />
                                    <div className="min-w-0">
                                        <h2 className="text-sm font-bold text-white tracking-tight truncate">Creative Strategy Dossier</h2>
                                        <p className="text-[10px] text-zinc-500 uppercase tracking-widest">
                                            {activeSectionLabel} · {savedAnalyses.find(a => a.id === selectedAnalysisId)?.inputs?.title || filmData.title || 'Untitled'}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                    <button 
                                        onClick={handleExportJSON}
                                        className="flex items-center gap-1.5 px-3 py-1.5 bg-zinc-800/80 hover:bg-zinc-700 text-zinc-300 text-[10px] font-bold uppercase tracking-wide rounded-lg transition-colors border border-zinc-700"
                                        title="Export JSON"
                                    >
                                        <FileJson size={11} /> JSON
                                    </button>
                                    <button 
                                        onClick={handleExportPDF}
                                        className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500/15 hover:bg-amber-500/25 text-amber-300 text-[10px] font-bold uppercase tracking-wide rounded-lg transition-colors border border-amber-500/30"
                                        title="Export PDF"
                                    >
                                        <Download size={11} /> PDF
                                    </button>
                                </div>
                            </div>

                            {/* Content: nav rail + section body */}
                            <div className="flex flex-1 overflow-hidden">
                                
                                {/* Vertical section nav */}
                                <div className="hidden lg:flex flex-col border-r border-zinc-800/60 px-3 py-4 flex-shrink-0">
                                    <SectionNav active={activeReportTab} onChange={setActiveReportTab} />
                                </div>

                                {/* Mobile: horizontal nav */}
                                <div className="lg:hidden absolute bottom-0 left-0 right-0 z-20 bg-zinc-950/95 backdrop-blur-sm border-t border-zinc-800 px-4 py-3">
                                    <div className="flex gap-2 overflow-x-auto pb-1 custom-scrollbar">
                                        {REPORT_SECTIONS.map(({ key, label, icon: Icon }) => (
                                            <button 
                                                key={key}
                                                onClick={() => setActiveReportTab(key)}
                                                className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-[10px] font-bold uppercase tracking-wide whitespace-nowrap border transition-all flex-shrink-0 ${
                                                    activeReportTab === key 
                                                        ? 'bg-amber-500/20 text-amber-300 border-amber-500/30' 
                                                        : 'bg-zinc-900 text-zinc-500 border-zinc-800'
                                                }`}
                                            >
                                                <Icon size={10} /> {label}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Section body — the only scrolling region */}
                                <div className="flex-1 overflow-y-auto custom-scrollbar">
                                    <div className="p-6 pb-20 lg:pb-6 animate-in fade-in slide-in-from-bottom-2 duration-300" key={activeReportTab}>
                                        {renderSection()}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default AudienceAnalysisTab;